import { memo, useCallback, useEffect, useRef } from 'react';
import parse from 'html-react-parser';

import {
  bridgeAtom,
  chatFeedbackAtom,
  errorAtom,
  isLoadingMessageAtom,
  isMlAnsweredAtom,
  isOpenChatAtom,
  isReadyForScrollAtom,
  isUserAnsweredAtom,
  messageTextAtom,
  useAtom,
  useValue,
} from '@/store';

import { Input, MESSAGE_LIMIT, PromptButton, SendButton, useKeyboard, useTheme } from '@/modules/common';
import { useBehaviour } from '@/modules/context';
import { Maintenance } from '@/modules/maintenance/Maintenance';
import { Messages, MessagesProps } from '@/modules/messages';

import {
  CloseButton,
  CloseButtonContainer,
  Footer,
  Header,
  IconContainer,
  InputContainer,
  PoweredBy,
  PromtsContainer,
  Star,
  Wrapper,
} from './Content.styles';

export type DesktopProps = MessagesProps;

export const Content = memo<DesktopProps>((props) => {
  const { sendMessage, resendMessage, removeMessage } = props;

  const theme = useTheme();
  const isMobile = window.matchMedia(`(${theme.mediaMobile})`).matches;

  const isOpenChat = useValue(isOpenChatAtom);
  const { translations, isEmbedded, prompts } = useValue(bridgeAtom);
  const chatFeedback = useValue(chatFeedbackAtom);
  const isLoadingMessage = useValue(isLoadingMessageAtom);
  const error = useValue(errorAtom);
  const isMlAnswered = useValue(isMlAnsweredAtom);
  const isUserAnswered = useValue(isUserAnsweredAtom);

  const [messageText, setMessageText] = useAtom(messageTextAtom);
  const [isReadyForScroll, setIsReadyForScroll] = useAtom(isReadyForScrollAtom);

  const { toggleChat } = useBehaviour();
  const { isOpenKeyboard } = useKeyboard();

  const isFirstRender = useRef(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLUListElement | null>(null);

  const onChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessageText(e.target.value);
    },
    [setMessageText]
  );

  const onTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (!isMlAnswered || isReadyForScroll || event.propertyName !== 'height') {
        return;
      }

      setIsReadyForScroll(true);
    },
    [isMlAnswered, isReadyForScroll, setIsReadyForScroll]
  );

  useEffect(() => {
    const onResize = () => {
      isFirstRender.current = true;
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const container = contentRef.current;
    const messages = messagesRef.current;

    if (!container || !messages) {
      return;
    }

    const handleTouchMove = (e: TouchEvent) => {
      const target = e.target as Node;
      const canScroll = messages.scrollHeight > messages.clientHeight;

      if (canScroll && messages.contains(target)) {
        return;
      }

      e.preventDefault();
    };

    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    return () => {
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, [messagesRef]);

  useEffect(() => {
    if (!isMlAnswered || isReadyForScroll || (!isMobile && !isEmbedded)) {
      return;
    }

    setIsReadyForScroll(true);
  }, [isMlAnswered, isReadyForScroll, setIsReadyForScroll, isMobile, isEmbedded]);

  if (isOpenChat) {
    isFirstRender.current = false;
  }

  const hasChatFeedback = Boolean(chatFeedback);

  const disableSendButton = isLoadingMessage || hasChatFeedback;
  const isDisabled = isLoadingMessage || error !== null;

  if (isEmbedded) {
    return (
      <Wrapper
        ref={contentRef}
        isOpen={isOpenChat}
        isOpenKeyboard={isOpenKeyboard}
        isFirstRender={isFirstRender.current}
        isEmbedded={isEmbedded}
      >
        <Messages
          sendMessage={sendMessage}
          resendMessage={resendMessage}
          removeMessage={removeMessage}
          ref={messagesRef}
        />

        <Footer hasChatFeedback={hasChatFeedback}>
          <InputContainer>
            <Input
              value={messageText}
              maxLength={MESSAGE_LIMIT}
              disabled={error !== null}
              focus={messageText.length === 0}
              placeholder={window.LLL(translations.inputPlaceholder)}
              onSend={sendMessage}
              onChange={onChangeInput}
            />
            <SendButton
              title={window.LLL(translations.sendMessageButton)}
              disabled={disableSendButton}
              onClick={sendMessage}
            />
          </InputContainer>

          <PromtsContainer>
            {prompts?.map(({ caption, prompt, iconUrl, eventName }, index) => (
              <PromptButton
                key={index}
                icon={iconUrl}
                disabled={isDisabled}
                prompt={prompt || caption}
                eventName={eventName}
              >
                {caption}
              </PromptButton>
            ))}
          </PromtsContainer>

          <PoweredBy>
            <span>{`${window.LLL(translations.poweredBy)} `}</span>
            <a href="https://saywhatt.com/" target="_blank" rel="noreferrer">
              <b>saywhatt</b>
            </a>
          </PoweredBy>
        </Footer>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      ref={contentRef}
      isOpen={isOpenChat}
      isOpenKeyboard={isOpenKeyboard}
      isFirstRender={isFirstRender.current}
      isMlAnswered={isMlAnswered}
      onTransitionEnd={onTransitionEnd}
    >
      <Header>
        <IconContainer isRtl={theme.isRtl}>
          <Star size={16} />
          <Star size={12} />
        </IconContainer>

        {parse(window.LLL(translations.title))}

        <CloseButtonContainer isRtl={theme.isRtl} onClick={toggleChat} role="button" aria-label="close button">
          <CloseButton />
        </CloseButtonContainer>
      </Header>

      <Messages
        sendMessage={sendMessage}
        resendMessage={resendMessage}
        removeMessage={removeMessage}
        ref={messagesRef}
      />

      <Footer hasChatFeedback={hasChatFeedback}>
        <InputContainer>
          <Input
            value={messageText}
            maxLength={MESSAGE_LIMIT}
            disabled={error !== null}
            focus={messageText.length === 0}
            placeholder={window.LLL(translations.inputPlaceholder)}
            onSend={sendMessage}
            onChange={onChangeInput}
          />
          <SendButton
            title={window.LLL(translations.sendMessageButton)}
            disabled={disableSendButton}
            onClick={sendMessage}
          />
        </InputContainer>

        {!isUserAnswered && (
          <PromtsContainer>
            {prompts?.map(({ caption, prompt, iconUrl, eventName, translationId }) => {
              const promptTranslation = translations.prompts[translationId];
              const captionText = promptTranslation?.caption ? window.LLL(promptTranslation.caption) : caption;
              const promptText = promptTranslation?.prompt ? window.LLL(promptTranslation.prompt) : prompt;

              return (
                <PromptButton
                  icon={iconUrl}
                  disabled={isDisabled}
                  prompt={promptText || captionText}
                  eventName={eventName}
                >
                  {captionText}
                </PromptButton>
              );
            })}
          </PromtsContainer>
        )}

        <PoweredBy>
          <span>{`${window.LLL(translations.poweredBy)} `}</span>
          <a href="https://saywhatt.com/" target="_blank" rel="noreferrer">
            <b>saywhatt</b>
          </a>
        </PoweredBy>
      </Footer>

      <Maintenance />
    </Wrapper>
  );
});
